import { normalizeText, sharedWordShare } from "@opulancia/renderer/editorial/qa";
import { COVER_MECHANISMS } from "@opulancia/renderer/editorial/instructions";
import type { Slide } from "../studio/model.js";
import { COVER_CHECKS, COVER_CHECKS_VERSION, IMAGE_CHECKS, newCoverStudy, type CoverCheck, type CoverCheckId, type CoverConcept, type CoverStudy, type Preparation } from "./model.js";
import type { Envelope, Hook, Issue } from "./scenario.js";

/**
 * The cover comparator (docs/PREPARER_UN_CARROUSEL.md, « Couvertures »): the three concepts of
 * the answer compared as finished covers before the project is built. Nothing here judges a
 * cover: no score, no ranking, no recommendation. It checks that the three concepts are three
 * different ideas, draws each concept on the Atelier's own cover, measures the title's contrast
 * on that drawing, and turns a member's answers and choice into what the project keeps.
 */

export const MECHANISM_LABEL: Record<string, string> = {
  contradiction: "Contradiction apparente",
  comparaison: "Comparaison",
  echelle: "Échelle surprenante",
  detail: "Détail révélateur",
  instant: "Instant suspendu",
  choix: "Choix personnel",
  acces: "Accès rare",
  autre: "Autre",
};

/** Two concepts this close in their words are likely the same idea twice. */
export const SIMILAR_CONCEPTS = 0.6;

/** Words that place something low in the frame, where the title, the fade and the arrow go. */
const LOW_IN_FRAME = /\b(tiers bas|tiers inferieur|bas de l image|bas du cadre|bas de l ecran|en bas|partie basse|moitie basse|moitie inferieure|partie inferieure|au pied de l image)\b/;

/** Where a concept's brief seems to put what carries the hook low in the frame (a hint, never a block: the words may mean something else). */
export function anomalyLow(h: Hook): string | null {
  const b = h.cover_brief;
  if (!b) return null;
  for (const text of [b.anomaly, b.framing, b.situation, b.subject]) {
    const m = text ? normalizeText(text).match(LOW_IN_FRAME) : null;
    if (m) return m[0];
  }
  return null;
}

const conceptWords = (h: Hook) => [h.visual_concept, h.cover_brief?.anomaly ?? "", h.cover_brief?.visual_question ?? ""].join(" ");

/** Warnings on the three concepts: the same mechanism twice, two concepts telling the same thing, what a /2 answer left out. */
export function coverConceptIssues(envelope: Envelope): Issue[] {
  const issues: Issue[] = [];
  const hooks = envelope.hooks;
  if (envelope.format === "opulancia-preparation/1") {
    issues.push({ level: "info", code: "COVER_V1", message: "Réponse au format /1 : les concepts n'indiquent ni mécanisme ni brief de couverture. Le comparateur fonctionne quand même." });
  } else {
    hooks.forEach((h, i) => {
      const missing = [!h.mechanism && "mécanisme", !h.surprise && "surprise", !h.cover_brief && "brief de couverture"].filter(Boolean);
      if (missing.length) issues.push({ level: "info", code: "COVER_INCOMPLETE", message: `Concept ${i + 1} : ${missing.join(", ")} non précisé(s).` });
      const low = anomalyLow(h);
      if (low)
        issues.push({
          level: "warning",
          code: "COVER_ANOMALY_LOW",
          message: `Concept ${i + 1} : le brief semble placer ce qui porte l'accroche dans le bas de l'image (« ${low} »), là où se posent le titre, le dégradé et la flèche. À vérifier avec « Afficher les zones » du comparateur ; si c'est le cas, cadre plus haut ou place le titre en haut.`,
        });
    });
  }
  for (let i = 0; i < hooks.length; i++) {
    for (let j = i + 1; j < hooks.length; j++) {
      const a = hooks[i]!;
      const b = hooks[j]!;
      if (a.mechanism && a.mechanism !== "autre" && a.mechanism === b.mechanism) {
        issues.push({ level: "warning", code: "COVER_SAME_MECHANISM", message: `Concepts ${i + 1} et ${j + 1} : même mécanisme (« ${MECHANISM_LABEL[a.mechanism]} »). Trois concepts = trois façons de raconter le sujet.` });
      }
      const share = sharedWordShare(conceptWords(a), conceptWords(b));
      if (share >= SIMILAR_CONCEPTS) {
        issues.push({ level: "warning", code: "COVER_SIMILAR", message: `Concepts ${i + 1} et ${j + 1} se ressemblent (${Math.round(share * 100)} % de mots communs) : sans doute la même idée deux fois.` });
      }
    }
  }
  return issues;
}

/** The comparator's state, created when it is first used (one concept per hook). */
export const studyOf = (prep: Preparation): CoverStudy => prep.cover_study ?? newCoverStudy();

/** The cover a concept gives: the scenario's first slide with the concept's title, accent, image and framing. */
export function conceptSlide(base: Slide, title: string, accent: string | undefined, concept: CoverConcept): Slide {
  return {
    ...structuredClone(base),
    title,
    accent: accent ? [accent] : [],
    highlight: "block",
    image: concept.image ? structuredClone(concept.image) : null,
    ...concept.frame,
  };
}

/** Questions that can be answered for a concept (without an image, the image questions cannot). */
export const answerable = (concept: CoverConcept, id: CoverCheckId) => !!concept.image || !IMAGE_CHECKS.has(id);

/** How far a concept's checklist got: answered (of answerable), yes, and how many need its image. */
export function conceptProgress(concept: CoverConcept): { answered: number; answerable: number; yes: number; waitingImage: number } {
  const ids = COVER_CHECKS.map(([id]) => id);
  const open = ids.filter((id) => answerable(concept, id));
  return {
    answered: open.filter((id) => concept.answers[id]).length,
    answerable: open.length,
    yes: open.filter((id) => concept.answers[id] === "oui").length,
    waitingImage: ids.length - open.length,
  };
}

/** The editor's cover check from the chosen concept's answers (a « non vérifiable » or « incertain » stays unchecked). */
export function coverCheckFrom(concept: CoverConcept, by: string, at: string, fingerprint: string): CoverCheck | null {
  if (!Object.keys(concept.answers).length) return null;
  return {
    by,
    at,
    fingerprint,
    version: COVER_CHECKS_VERSION,
    note: concept.glance ? `Coup d'œil : ${concept.glance}` : "",
    items: Object.fromEntries(COVER_CHECKS.map(([id]) => [id, answerable(concept, id) && concept.answers[id] === "oui"])) as Record<CoverCheckId, boolean>,
  };
}

const linear = (c: number) => {
  const v = c / 255;
  return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
};
const luminance = (r: number, g: number, b: number) => 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);

/**
 * The white title's contrast with what is under it, measured on the drawn cover (WCAG 2.1
 * contrast ratio). `full` is the cover as drawn, `background` the same cover without its title.
 * A pixel belongs to the title when it is white in `full` and differs from `background`; the
 * ochre block's black letters are not white, so they are left out (their contrast is fixed).
 * A measure of legibility, not of interest: it says nothing of whether the cover stops anyone.
 */
export function titleContrast(full: Uint8ClampedArray, background: Uint8ClampedArray, step = 2): { median: number; low: number; pixels: number } | null {
  const ratios: number[] = [];
  for (let i = 0; i < full.length; i += 4 * step) {
    const r = full[i]!;
    const g = full[i + 1]!;
    const b = full[i + 2]!;
    if (r < 235 || g < 235 || b < 235) continue;
    const br = background[i]!;
    const bg = background[i + 1]!;
    const bb = background[i + 2]!;
    if (Math.abs(r - br) + Math.abs(g - bg) + Math.abs(b - bb) < 60) continue;
    ratios.push(1.05 / (luminance(br, bg, bb) + 0.05));
  }
  if (!ratios.length) return null;
  ratios.sort((x, y) => x - y);
  return { median: ratios[Math.floor(ratios.length / 2)]!, low: ratios[Math.floor(ratios.length * 0.1)]!, pixels: ratios.length };
}

/** The mechanisms as the comparator shows them (the shared instructions' texts). */
export const mechanismText = (id: string | undefined) => (id ? (COVER_MECHANISMS.find((m) => m.id === id)?.fr ?? (id === "autre" ? "autre mécanisme (voir la surprise)" : id)) : null);

export { COVER_CHECKS, IMAGE_CHECKS };
